/**
 * Sprint T-16.1 · Automation Center · React Query hooks.
 *
 * 1 endpoint = 1 hook. NO derived state. NO calculations.
 * Auto-refresh policy (§17.4 approved):
 *   running    → 5s
 *   queue      → 10s
 *   overview   → 30s
 *   metrics    → 30s
 *   events     → manual (opt-in via refetch)
 *   rest       → manual
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { automationApi } from "../services/automationApi";

const FIVE_SEC = 5 * 1000;
const TEN_SEC = 10 * 1000;
const THIRTY_SEC = 30 * 1000;
const ONE_MIN = 60 * 1000;
const FIVE_MIN = 5 * 60 * 1000;

const baseOpts = {
  staleTime: TEN_SEC,
  gcTime: FIVE_MIN,
  retry: 1,
  refetchOnWindowFocus: false,
};

// ---------------------------------------------------------------------------
// 1. Engine info
// ---------------------------------------------------------------------------

export const useAutomationInfo = () =>
  useQuery({
    queryKey: ["automation.info"],
    queryFn: () => automationApi.info().then((r) => r.data),
    ...baseOpts,
    staleTime: THIRTY_SEC,
    refetchInterval: THIRTY_SEC,
  });

// ---------------------------------------------------------------------------
// 2-3. Jobs
// ---------------------------------------------------------------------------

export const useAutomationJobs = () =>
  useQuery({
    queryKey: ["automation.jobs"],
    queryFn: () => automationApi.listJobs().then((r) => r.data),
    ...baseOpts,
    staleTime: ONE_MIN,
  });

export const useAutomationJob = (jobId) =>
  useQuery({
    queryKey: ["automation.job", jobId],
    queryFn: () => automationApi.getJob(jobId).then((r) => r.data),
    ...baseOpts,
    enabled: Boolean(jobId),
    staleTime: THIRTY_SEC,
  });

// ---------------------------------------------------------------------------
// 4-6. Job mutations
// ---------------------------------------------------------------------------

export const useRunJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, input, idempotency_key }) =>
      automationApi.runJob(jobId, { input, idempotency_key }).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["automation.job", vars.jobId] });
      qc.invalidateQueries({ queryKey: ["automation.executions"] });
      qc.invalidateQueries({ queryKey: ["automation.metrics"] });
    },
  });
};

export const usePauseJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, until }) =>
      automationApi.pauseJob(jobId, until).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["automation.jobs"] });
      qc.invalidateQueries({ queryKey: ["automation.job", vars.jobId] });
    },
  });
};

export const useResumeJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId }) =>
      automationApi.resumeJob(jobId).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["automation.jobs"] });
      qc.invalidateQueries({ queryKey: ["automation.job", vars.jobId] });
    },
  });
};

// ---------------------------------------------------------------------------
// 7-8. Executions
// ---------------------------------------------------------------------------

/** Generic executions list. `poll` overrides refetchInterval (e.g. running=5s, queue=10s). */
export const useExecutions = (params = {}, { poll = null } = {}) =>
  useQuery({
    queryKey: ["automation.executions", params],
    queryFn: () => automationApi.listExecutions(params).then((r) => r.data),
    ...baseOpts,
    refetchInterval: poll || false,
  });

export const useExecutionDetail = (id) =>
  useQuery({
    queryKey: ["automation.execution", id],
    queryFn: () => automationApi.getExecution(id).then((r) => r.data),
    ...baseOpts,
    enabled: Boolean(id),
  });

// ---------------------------------------------------------------------------
// 9-11. DLQ
// ---------------------------------------------------------------------------

export const useDlq = (params = {}) =>
  useQuery({
    queryKey: ["automation.dlq", params],
    queryFn: () => automationApi.listDlq(params).then((r) => r.data),
    ...baseOpts,
  });

export const useDlqRetry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dlqId) => automationApi.retryDlq(dlqId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["automation.dlq"] });
      qc.invalidateQueries({ queryKey: ["automation.executions"] });
    },
  });
};

export const useDlqDiscard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dlqId) => automationApi.discardDlq(dlqId).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automation.dlq"] }),
  });
};

// ---------------------------------------------------------------------------
// 12. Events
// ---------------------------------------------------------------------------

export const useEvents = (params = {}) =>
  useQuery({
    queryKey: ["automation.events", params],
    queryFn: () => automationApi.listEvents(params).then((r) => r.data),
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// 13. Notification intents
// ---------------------------------------------------------------------------

export const useNotificationIntents = (params = {}) =>
  useQuery({
    queryKey: ["automation.intents", params],
    queryFn: () => automationApi.listIntents(params).then((r) => r.data),
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// 14. Metrics
// ---------------------------------------------------------------------------

export const useAutomationMetrics = () =>
  useQuery({
    queryKey: ["automation.metrics"],
    queryFn: () => automationApi.metrics().then((r) => r.data),
    ...baseOpts,
    staleTime: THIRTY_SEC,
    refetchInterval: THIRTY_SEC,
  });
